import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, STATUS_STYLES } from "@/components/StatusBadge";

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div data-testid="reports-loading" className="py-20 text-center text-sm text-muted-foreground">Compiling reports…</div>;

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">Business growth, pipeline conversion and venue performance.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="border border-border bg-white p-5" data-testid="report-total-bookings">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Total Bookings</p>
          <p className="mt-2 font-heading text-3xl font-black">{data.total_bookings}</p>
        </div>
        <div className="border border-border bg-white p-5" data-testid="report-avg-value">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Avg Booking Value</p>
          <p className="mt-2 font-heading text-3xl font-black text-brand">{fmt(data.avg_booking_value)}</p>
        </div>
        <div className="border border-border bg-white p-5" data-testid="report-conversion">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Enquiry Conversion</p>
          <p className="mt-2 font-heading text-3xl font-black">{data.conversion_rate}%</p>
        </div>
        <div className="border border-border bg-white p-5" data-testid="report-pipeline">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Open Enquiries</p>
          <p className="mt-2 font-heading text-3xl font-black">{(data.enquiry_funnel.find((f) => f.stage === "new")?.count || 0) + (data.enquiry_funnel.find((f) => f.stage === "quoted")?.count || 0)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="border border-border bg-white p-5" data-testid="revenue-chart-card">
          <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Revenue by Event Month</h2>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenue_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="revenue" fill="#059669" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-border bg-white p-5" data-testid="funnel-card">
            <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Enquiry Funnel</h2>
            <div className="mt-4 space-y-2">
              {data.enquiry_funnel.map((f) => (
                <div key={f.stage} className="flex items-center justify-between text-sm">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] ${STATUS_STYLES[f.stage] || ""}`}>{f.stage}</span>
                  <span className="font-bold tabular-nums" data-testid={`funnel-${f.stage}`}>{f.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border bg-white" data-testid="top-venues-card">
            <div className="border-b border-border px-5 py-3">
              <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Top Venues by Revenue</h2>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.top_venues.map((v) => (
                  <tr key={v.venue} className="border-b border-border last:border-0">
                    <td className="px-5 py-2.5 font-medium">{v.venue}</td>
                    <td className="px-5 py-2.5 text-muted-foreground tabular-nums">{v.events} events</td>
                    <td className="px-5 py-2.5 text-right font-semibold text-brand tabular-nums">{fmt(v.revenue)}</td>
                  </tr>
                ))}
                {data.top_venues.length === 0 && <tr><td className="px-5 py-6 text-center text-muted-foreground">No venue data yet.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
